import * as PublicWifiActions from './../actions/public-wifi.actions';
import { TechnicalError } from '@dch/products-business-logic';

export interface PublicWifiAccount {
  login: string;
  password: string;
  technicalLoginId: string;
}

export interface PublicWifiState {
  jwt: any;
  isPendingProducts: boolean;
  errorProducts: TechnicalError;
  accounts: Array<PublicWifiAccount>;
  isPendingAccounts: boolean;
  errorAccounts: TechnicalError;
  accountsSuccess: boolean;
  selectedTechnicalLoginId: string;
  updateAliasSuccess: boolean;
  updateAliasError: boolean;
  updateAliasPending: boolean;
  createAliasSuccess: boolean;
  createAliasError: boolean;
  createAliasPending: boolean;
  updatePasswordSuccess: boolean;
  updatePasswordError: boolean;
  updatePasswordPending: boolean;
  userOwnMsisdn: boolean;
}

export const initialState: PublicWifiState = {
  jwt: null,
  isPendingProducts: false,
  errorProducts: null,
  accounts: null,
  isPendingAccounts: false,
  errorAccounts: null,
  accountsSuccess: false,
  selectedTechnicalLoginId: null,
  updateAliasSuccess: false,
  updateAliasError: false,
  updateAliasPending: false,
  createAliasSuccess: false,
  createAliasError: false,
  createAliasPending: false,
  updatePasswordSuccess: false,
  updatePasswordError: false,
  updatePasswordPending: false,
  userOwnMsisdn: true,
};

export function reducer(
  state: PublicWifiState = initialState,
  action: PublicWifiActions.Actions,
): PublicWifiState {
  switch (action.type) {
    case PublicWifiActions.GET_PUBLIC_WIFI_PRODUCTS:
      return {
        ...state,
        jwt: null,
        isPendingProducts: true,
        errorProducts: null,
      };
    case PublicWifiActions.GET_PUBLIC_WIFI_PRODUCTS_SUCCESS:
      return {
        ...state,
        jwt: action.payload['x-application-authorization-products-data'],
        isPendingProducts: false,
        errorProducts: null,
      };
    case PublicWifiActions.GET_PUBLIC_WIFI_PRODUCTS_FAILED:
      return {
        ...state,
        jwt: null,
        isPendingProducts: false,
        errorProducts: action.payload,
      };
    case PublicWifiActions.GET_PUBLIC_WIFI_ACCOUNTS:
      return {
        ...state,
        accounts: null,
        isPendingAccounts: true,
        errorAccounts: null,
        accountsSuccess: false,
        userOwnMsisdn: action.payload.ownedMsisdn,
      };
    case PublicWifiActions.GET_PUBLIC_WIFI_ACCOUNTS_SUCCESS:
      return {
        ...state,
        accounts: action.payload.accounts,
        isPendingAccounts: false,
        errorAccounts: null,
        accountsSuccess: true,
      };
    case PublicWifiActions.GET_PUBLIC_WIFI_ACCOUNTS_FAILED:
      return {
        ...state,
        accounts: null,
        isPendingAccounts: false,
        errorAccounts: action.payload,
        accountsSuccess: false,
      };
    case PublicWifiActions.CREATE_PUBLIC_WIFI_LOGIN_ALIAS:
      return {
        ...state,
        selectedTechnicalLoginId: action.payload.technicalLoginId,
        createAliasPending: true,
        createAliasSuccess: false,
        createAliasError: false,
      };
    case PublicWifiActions.CREATE_PUBLIC_WIFI_LOGIN_ALIAS_SUCCESS:
      return {
        ...state,
        createAliasPending: false,
        createAliasSuccess: true,
        createAliasError: false,
      };
    case PublicWifiActions.CREATE_PUBLIC_WIFI_LOGIN_ALIAS_FAILED:
      return {
        ...state,
        createAliasPending: false,
        createAliasSuccess: false,
        createAliasError: true,
      };
    case PublicWifiActions.UPDATE_PUBLIC_WIFI_LOGIN_ALIAS:
      return {
        ...state,
        selectedTechnicalLoginId: action.payload.technicalLoginId,
        updateAliasPending: true,
        updateAliasSuccess: false,
        updateAliasError: false,
      };
    case PublicWifiActions.UPDATE_PUBLIC_WIFI_LOGIN_ALIAS_SUCCESS:
      return {
        ...state,
        updateAliasPending: false,
        updateAliasSuccess: true,
        updateAliasError: false,
      };
    case PublicWifiActions.UPDATE_PUBLIC_WIFI_LOGIN_ALIAS_FAILED:
      return {
        ...state,
        updateAliasPending: false,
        updateAliasSuccess: false,
        updateAliasError: true,
      };
    case PublicWifiActions.UPDATE_PUBLIC_WIFI_PASSWORD:
      return {
        ...state,
        selectedTechnicalLoginId: action.payload.technicalLoginId,
        updatePasswordPending: true,
        updatePasswordSuccess: false,
        updatePasswordError: false,
      };
    case PublicWifiActions.UPDATE_PUBLIC_WIFI_PASSWORD_SUCCESS:
      return {
        ...state,
        updatePasswordPending: false,
        updatePasswordSuccess: true,
        updatePasswordError: false,
      };
    case PublicWifiActions.UPDATE_PUBLIC_WIFI_PASSWORD_FAILED:
      return {
        ...state,
        updatePasswordPending: false,
        updatePasswordSuccess: false,
        updatePasswordError: true,
      };
    case PublicWifiActions.RESET_PUBLIC_WIFI_MANAGE_VALUES:
      return {
        ...state,
        selectedTechnicalLoginId: null,
        updateAliasPending: false,
        updateAliasSuccess: false,
        updateAliasError: false,
        updatePasswordPending: false,
        updatePasswordSuccess: false,
        updatePasswordError: false,
      };
    case PublicWifiActions.RESET_PUBLIC_WIFI_CREATE_VALUES:
      return {
        ...state,
        selectedTechnicalLoginId: null,
        createAliasPending: false,
        createAliasSuccess: false,
        createAliasError: false,
      };

    default:
      return state;
  }
}

export const getProductsJwt = (state: PublicWifiState) => state.jwt;
export const getProductsError = (state: PublicWifiState) => state.errorProducts;
export const getProductsPending = (state: PublicWifiState) =>
  state.isPendingProducts;

export const getAccounts = (state: PublicWifiState) => state.accounts;
export const getAccountsSuccess = (state: PublicWifiState) =>
  state.accountsSuccess;
export const getAccountsError = (state: PublicWifiState) => state.errorAccounts;
export const getAccountsPending = (state: PublicWifiState) =>
  state.isPendingAccounts;
export const isUserOwningMsisdn = (state: PublicWifiState) =>
  state.userOwnMsisdn;

export const getCreateAliasPending = (state: PublicWifiState) => ({
  value: state.createAliasPending,
  selectedTechnicalLoginId: state.selectedTechnicalLoginId,
});
export const getCreateAliasSuccess = (state: PublicWifiState) => ({
  value: state.createAliasSuccess,
  selectedTechnicalLoginId: state.selectedTechnicalLoginId,
});
export const getCreateAliasError = (state: PublicWifiState) => ({
  value: state.createAliasError,
  selectedTechnicalLoginId: state.selectedTechnicalLoginId,
});

export const getUpdateAliasPending = (state: PublicWifiState) => ({
  value: state.updateAliasPending,
  selectedTechnicalLoginId: state.selectedTechnicalLoginId,
});
export const getUpdateAliasSuccess = (state: PublicWifiState) => ({
  value: state.updateAliasSuccess,
  selectedTechnicalLoginId: state.selectedTechnicalLoginId,
});
export const getUpdateAliasError = (state: PublicWifiState) => ({
  value: state.updateAliasError,
  selectedTechnicalLoginId: state.selectedTechnicalLoginId,
});

export const getUpdatePasswordPending = (state: PublicWifiState) => ({
  value: state.updatePasswordPending,
  selectedTechnicalLoginId: state.selectedTechnicalLoginId,
});
export const getUpdatePasswordSuccess = (state: PublicWifiState) => ({
  value: state.updatePasswordSuccess,
  selectedTechnicalLoginId: state.selectedTechnicalLoginId,
});
export const getUpdatePasswordError = (state: PublicWifiState) => ({
  value: state.updatePasswordError,
  selectedTechnicalLoginId: state.selectedTechnicalLoginId,
});
